import { useEffect, useState, useRef, useContext } from 'react';
import { useTheme } from '@stores/useTheme';
import FilterButton from '@components/StatsConsole/Widgets/common/FilterButton';
import { LeftArrowInChipsFilters } from '@components/StatsConsole/Layout/parts/ChipsFiltersarrows/LeftArrowInChipsFilters';
import { RightArrowInChipsFilters } from '@components/StatsConsole/Layout/parts/ChipsFiltersarrows/RightArrowInChipsFilters';
import { IState, StateContext } from "../../../../../state/stateContext";
import { ActiveFilterName, T } from "@utils/enums";
import { useLanguage } from '@utils/hooks/useLanguage';
import { useTranslations } from '../../../../../translation/TranslationContext';
import { getEnumKeyByValue, getIsMobileDevice } from '@utils/functions';

interface IFiltersStatsProps {
    statsTabData: any;
}

const FiltersStats = ({ statsTabData }: IFiltersStatsProps) => {
    const { theme } = useTheme();
    const ctx = useContext(StateContext);
    const { stContext, setStContext } = ctx || {};
    const { statsTabActiveFilter } = stContext || {};

    const language = useLanguage();
    const lang = useTranslations(language);

    const menuItemsSTRef = useRef<HTMLDivElement>(null);
    const [isMenuArrowsVisible, setIsMenuArrowsVisible] = useState(false);
    const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(true);
    const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(true);
    const [isMenuItemsScrollable, setIsMenuItemsScrollable] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const ActiveFilter = {
        ALL: lang['InHouseStats.StatsAll'],
        FIRST_HALF: lang['InHouseStats.StatsFirstHalf'],
        SECOND_HALF: lang['InHouseStats.StatsSecondHalf'],
        OVERTIME: lang['InHouseStats.StatsOvertime']
    } as const;

    type ActiveFilterKey = keyof typeof ActiveFilter;

    const setActiveFilter = (filter: ActiveFilterKey | null) => {
        setStContext((prev: IState) => ({
            ...prev,
            statsTabActiveFilter: filter,
            statsTabActiveFilterName: 
                filter === getEnumKeyByValue(ActiveFilter, ActiveFilter.ALL) ? ActiveFilterName.FULL_MATCH :
                filter === getEnumKeyByValue(ActiveFilter, ActiveFilter.FIRST_HALF) ? ActiveFilterName.FIRST_HALF :
                filter === getEnumKeyByValue(ActiveFilter, ActiveFilter.SECOND_HALF) ? ActiveFilterName.SECOND_HALF :
                ActiveFilterName.OVERTIME
        }))
    }

    const onClickLeftArrow = () => {
        const menuItemsContainer = menuItemsSTRef.current;
        if (menuItemsContainer) {
            const currentScroll = menuItemsContainer.scrollLeft;
            const scrollAmount = Math.min(40, currentScroll);
            menuItemsContainer.scrollLeft -= scrollAmount;
        }
    }

    const onClickRightArrow = () => {
        const menuItemsContainer = menuItemsSTRef.current;
        if (menuItemsContainer) {
            const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
            const currentScroll = menuItemsContainer.scrollLeft;
            const remainingScroll = maxScroll - currentScroll;
            const scrollAmount = Math.min(40, remainingScroll);
            menuItemsContainer.scrollLeft += scrollAmount;
        }
    }

    useEffect(() => {
        const isMobileDevice = getIsMobileDevice();
        if (isMobileDevice) {
            setIsMenuArrowsVisible(false); 
        } else {
            if (isMenuItemsScrollable) {
                setIsMenuArrowsVisible(true);
            } else {
                setIsMenuArrowsVisible(false);
            }
        }
    }, [isMenuItemsScrollable])

    useEffect(() => {
        const menuItemsContainer = menuItemsSTRef.current;
        if (menuItemsContainer) {
            const handleScroll = () => {
                const currentScroll = menuItemsContainer.scrollLeft;
                const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
                setIsLeftArrowDisabled(currentScroll === 0);
                setIsRightArrowDisabled(Math.abs(currentScroll - maxScroll) < 1);
            };
            
            menuItemsContainer.addEventListener('scroll', handleScroll);
            handleScroll();
            
            return () => {
                menuItemsContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [window.innerWidth]);

    useEffect(() => {
        const checkScrollability = () => {
            if (menuItemsSTRef.current) {
                const itemsWidth = menuItemsSTRef.current.scrollWidth;
                const containerWidth = menuItemsSTRef.current.clientWidth;
                setIsMenuItemsScrollable(itemsWidth > containerWidth);
            } else {
                setIsMenuItemsScrollable(false);
            }
        };
    
        checkScrollability();
        window.addEventListener('resize', checkScrollability);
    
        return () => {
            window.removeEventListener('resize', checkScrollability);
        };
    }, [language, statsTabData]);

    const hasFilterData = (filterKey: string) => {
        const filterData = statsTabData?.[filterKey];
        return filterData && 
               filterData.statistics && 
               Array.isArray(filterData.statistics) && 
               filterData.statistics.length > 0;
    };

    return (
        <div className='flex flex-row justify-center items-center gap-[3px] w-full select-none'
             onMouseEnter={() => setIsHovered(true)}
             onMouseLeave={() => setIsHovered(false)}
             data-testid="stats-filters-container">
            {isMenuArrowsVisible && 
                <LeftArrowInChipsFilters
                    color={theme === T.LIGHT ? (isLeftArrowDisabled ? '#111111' : '#00531D') : (isLeftArrowDisabled ? '#111111' : '#00531D')}  
                    onClick={onClickLeftArrow} 
                    isVisibleOnHover={isHovered}
                    isArrowHidden={isLeftArrowDisabled}
                />
            }
            <div ref={menuItemsSTRef} 
                data-testid="stats-widget-filter-container"
                 className={`pl-[8px] flex min-w-full w-full items-center mb-[12px] mt-[8px]
                            overflow-x-scroll menuItemsStats-disable-scrollbar z-10 select-none
                            [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                            ${isMenuArrowsVisible && 'justify-start'}
                            ${!isMenuArrowsVisible && isMenuItemsScrollable && 'justify-start'}
                            ${!isMenuArrowsVisible && !isMenuItemsScrollable && 'justify-center'}`}>
                <FilterButton 
                    text={ActiveFilter.ALL} 
                    isActive={statsTabActiveFilter === getEnumKeyByValue(ActiveFilter, ActiveFilter.ALL)} 
                    onClick={() => setActiveFilter(getEnumKeyByValue(ActiveFilter, ActiveFilter.ALL))} 
                    additionalClassName={`mr-[8px]`}
                    id="filter-all"
                    disabled={!hasFilterData('full_match')}
                />
                <FilterButton 
                    text={ActiveFilter.FIRST_HALF} 
                    isActive={statsTabActiveFilter === getEnumKeyByValue(ActiveFilter, ActiveFilter.FIRST_HALF)} 
                    onClick={() => setActiveFilter(getEnumKeyByValue(ActiveFilter, ActiveFilter.FIRST_HALF))} 
                    additionalClassName={`mr-[8px]`}
                    id="filter-first-half"
                    disabled={!hasFilterData('first_half')}
                />
                <FilterButton 
                    text={ActiveFilter.SECOND_HALF} 
                    isActive={statsTabActiveFilter === getEnumKeyByValue(ActiveFilter, ActiveFilter.SECOND_HALF)} 
                    onClick={() => setActiveFilter(getEnumKeyByValue(ActiveFilter, ActiveFilter.SECOND_HALF))}
                    additionalClassName={`mr-[8px]`}
                    id="filter-second-half"
                    disabled={!hasFilterData('second_half')}
                />
                {hasFilterData('overtime') && (
                <FilterButton 
                    text={ActiveFilter.OVERTIME} 
                    isActive={statsTabActiveFilter === getEnumKeyByValue(ActiveFilter, ActiveFilter.OVERTIME)} 
                    onClick={() => setActiveFilter(getEnumKeyByValue(ActiveFilter, ActiveFilter.OVERTIME))}
                    additionalClassName={`${isMenuArrowsVisible ? 'mr-[8px]' : ''}`}
                    id="filter-overtime"
                    disabled={!hasFilterData('overtime')}
                />
                )}
            </div>
            {isMenuArrowsVisible && 
                <RightArrowInChipsFilters 
                    color={theme === T.LIGHT ? (isRightArrowDisabled ? '#111111' : '#00531D') : (isRightArrowDisabled ? '#111111' : '#00531D')} 
                    onClick={onClickRightArrow} 
                    isVisibleOnHover={isHovered}
                    isArrowHidden={isRightArrowDisabled}
                />
            }
        </div>
    );
};

export default FiltersStats;
